import React, { useEffect, useState } from 'react';
import { AutoComplete, Input, Modal, message } from 'antd';
import { useTranslation } from 'react-i18next';
import { useMutation } from '@apollo/client';
import ImageUploader from '../../../../components/ImageUploader';
import {
  ADDRESS_ABNORMAL_REASON_SUGGESTIONS,
  REPORT_ADDRESS_ABNORMAL,
  ReportAddressAbnormalInput,
  ReportAddressAbnormalResponse,
} from '../graphql/mutations/addressAbnormal';
import { MODAL_FOOTER_STYLE, MODAL_HEADER_STYLE, modalBodyStyle, modalTitle } from '../utils/modalChrome';
import { ReviewAbnormalReportData } from '../types/reviewPool';
import { translateGraphQLError } from '../../../../utils/graphqlUtils';

/** 本弹窗全部文案的 i18n 前缀。抽成常量是为了让报错兜底也能复用同一段，不写死两处。 */
const WT_PREFIX = 'pickup.addressReviewPool.abnormal';

interface Props {
  open: boolean;
  /** 展示用的目标地址（只读回显，让运营确认自己报的是哪个地址） */
  targetAddress: string;
  /** 审核池路径：条目 id。与 cooperationWarehouseId 二选一 */
  reviewPoolId?: number;
  /** 司机反馈跟进路径：站点 id。与 reviewPoolId 二选一 */
  cooperationWarehouseId?: number;
  /** 有值 = 编辑既有上报（预填原因/备注/凭证）；不传 = 新增上报 */
  existing?: ReviewAbnormalReportData;
  onClose: () => void;
  /**
   * 上报/编辑成功后回调，**带上刚提交的内容**。
   *
   * <p>调用方可以据此就地更新自己那份数据，不必为了拿回同样的值再拉一次接口 ——
   * 审核池侧（ReviewAddressDetail）走的就是这条路，因为重拉列表会把列表整体重置回第一页。
   * 站点侧（DriverFeedbackDetail）refetch 的是操作日志时间线，那不是这里能拼出来的，
   * 忽略这个参数即可（TS 允许实参更少的实现）。
   *
   * <p>`proofUrls` 是服务端随 mutation 返回的预签名可读地址（前端签不出来）。
   * **可能比 proofKeys 短**（个别 key 预签名失败），要么整组用、要么整组不用。
   */
  onSaved: (saved: {
    reason?: string;
    remark?: string;
    proofKeys: string[];
    proofUrls: string[];
  }) => void;
}

/**
 * 上报 / 编辑地址异常（2.2 需求点1）。
 *
 * <p>适用于马路地址、无门牌号等**上门必然失败**的地址：上报后跳过「安排司机上门」，
 * 直接进入二次建联；若二次仍未联系到客户，站点会被置为「确认无法建联」。
 *
 * <h3>三个字段里只有凭证必填</h3>
 * 原因与备注都是<b>自由输入且非必填</b>（原型：「请输入 / 选择」，备注 label 直接写「可选」）——
 * 现场情况穷举不完，限制取值只会逼运营选一个最接近的、反而失真。
 *
 * <p>于是<b>凭证成了这条上报唯一必然存在的证据</b>，而上报的后果是该地址免上门直达
 * 「确认无法建联」并停止揽收，事后抽检只能靠照片。所以必填的是它，不是原因。
 * 与后端 validateProof 同一判据（后端也校验，前端这道只是提前反馈）。
 *
 * <p>字段顺序按原型：<b>凭证 → 原因 → 备注</b>。把必填项放最前，运营不会填完两栏文字才发现还要传图。
 */
const ReportAddressAbnormalModal: React.FC<Props> = ({
  open,
  targetAddress,
  reviewPoolId,
  cooperationWarehouseId,
  existing,
  onClose,
  onSaved,
}) => {
  const { t } = useTranslation();
  const wt = (k: string) => t(`${WT_PREFIX}.${k}`);

  const [reason, setReason] = useState('');
  const [remark, setRemark] = useState('');
  const [proofKeys, setProofKeys] = useState<string[]>([]);

  const isEdit = !!existing;

  /**
   * 打开时取一次 existing 快照。弹窗复用同一实例，不重置会把上一次（或上一个条目）的
   * 原因与凭证带进来，运营以为自己在新报、实际提交的是残留值。
   *
   * <p><b>依赖里刻意只有 open，不含 existing</b>：三个调用方传的都是**每次 render 新建的对象字面量**
   * （见 ReviewAddressDetail / DriverFeedbackDetail），引用永远在变。把它放进依赖，
   * 父组件任意一次重渲染（时间线 refetch 返回、审核池列表刷新回抛新条目、切筛选）都会把运营
   * 已选的原因、已填的备注、已传的凭证清空 —— 而且无声无息，看起来像"我明明选了怎么没生效"。
   *
   * <p>打开期间不再跟随 existing 变化是**期望行为**：运营正在编辑，外部刷新不该覆盖他的输入。
   * 新增 vs 编辑由调用方在打开**之前**用 abnormalEditing 决定，不存在"先打开再等数据回来预填"的场景。
   */
  useEffect(() => {
    if (!open) return;
    setReason(existing?.reason ?? '');
    setRemark(existing?.remark ?? '');
    setProofKeys(existing?.proofKeys ?? []);
    // exhaustive-deps 报在依赖数组这一行，disable 注释必须贴在它上面 ——
    // 放在 useEffect( 那一行盖不住（本文件先前就是那样写的，警告一直在）。
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  const [reportMutation, { loading }] = useMutation<
    ReportAddressAbnormalResponse,
    { input: ReportAddressAbnormalInput }
  >(REPORT_ADDRESS_ABNORMAL, { fetchPolicy: 'no-cache' });

  /**
   * 把 displayUrls 补齐到与 keys 等长，缺的位置留空串。
   *
   * <p>`ImageUploader.fileListFromProps` 只在 `value.length === displayUrls.length` 时渲染既有项，
   * 否则**静默返回空列表**（maxCount>1 无兜底分支）。编辑态一旦再加一张图，长度就不等 ——
   * 后果是三张缩略图**全部消失**，运营以为原来的凭证被删了。
   *
   * <p>与 ReviewAddressDetail.padUrls 同口径（那里有完整的取舍说明）：补齐后那一项以裂图出现，
   * 但可见即可删、状态能自愈。刻意不抽成公共函数 —— 抽会改到那段正在跑的代码，
   * 3 行重复换零回归风险是值得的。也刻意不改共享 ImageUploader：它有 34 个调用方。
   */
  const padUrls = (keys: string[], urls?: string[]) => {
    const safe = urls ?? [];
    return keys.length === safe.length ? safe : keys.map((_, i) => safe[i] ?? '');
  };

  const handleOk = async () => {
    // 两个必填项。后端各有独立校验（PICKUP_ABNORMAL_PROOF_REQUIRED / PICKUP_ABNORMAL_REASON_REQUIRED），
    // 这里只是提前反馈 —— 前端校验不能替代后端校验，接口可以被直接调用。
    // 先判凭证：它在表单最上方，先提示离焦点更近的那个。
    if (!proofKeys.length) {
      message.error(wt('proofRequired'));
      return;
    }
    // 2026-09-09 产品调整为必填。只 trim 判空、不校验取值 —— 建议项不是白名单，与后端同口径
    if (!reason.trim()) {
      message.error(wt('reasonRequired'));
      return;
    }
    try {
      const resp = await reportMutation({
        variables: {
          input: {
            reviewPoolId,
            cooperationWarehouseId,
            reason: reason.trim() || undefined,
            remark: remark.trim() || undefined,
            proofS3Keys: proofKeys,
          },
        },
      });
      const result = resp.data?.reportAddressAbnormal;
      if (!result?.success) {
        message.error(wt('failed'));
        return;
      }
      message.success(isEdit ? wt('editSucceeded') : wt('reportSucceeded'));
      // 与 variables.input 同口径（同样 trim、同样空转 undefined），避免调用方就地更新出来的
      // 值与库里那份对不上。
      onSaved({
        reason: reason.trim() || undefined,
        remark: remark.trim() || undefined,
        proofKeys,
        proofUrls: result.proofDisplayUrls ?? [],
      });
      onClose();
    } catch (e: any) {
      // 后端按码区分了原因必填 / 凭证必填 / 状态不允许 / 目标不存在 / 并发冲突 / 灰度未开放，
      // 这 6 个 messageKey 的中英西文案已补齐在 i18n 的 pickup.abnormal.* 下，
      // 且 api-server 侧把它们登记进了 MessageKey 枚举才能落到 extensions.messageKey
      // （见 PickupReviewPoolMutation.orThrowKnownBizElseLogFailure）。三处必须同步增减。
      //
      // 仍用 translateGraphQLError 而不是裸 t(messageKey)：它会识别"取不到文案、原样返回 key"
      // 的情况并回落兜底，覆盖两种残留场景 —— ① pickup-service 新增了 ReportResult 但两侧还没登记；
      // ② 真异常走 pickup.review_pool.resolve_failed 这类未登记的码。
      //
      // 刻意不在这里 onClose()：失败时弹窗要留着，运营才能就地改内容重试。
      message.error(translateGraphQLError(e, `${WT_PREFIX}.failed`));
    }
  };

  return (
    <Modal
      open={open}
      title={modalTitle(isEdit ? wt('editTitle') : wt('reportTitle'))}
      onCancel={onClose}
      onOk={handleOk}
      confirmLoading={loading}
      okText={isEdit ? wt('saveCta') : wt('reportCta')}
      width={560}
      styles={{ header: MODAL_HEADER_STYLE, body: modalBodyStyle('60vh'), footer: MODAL_FOOTER_STYLE }}
    >
      <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
        <div
          style={{
            background: '#f8f5fe',
            border: '1px solid #ece5fb',
            borderRadius: 10,
            padding: '12px 14px',
            fontSize: 12.5,
            color: '#5b5768',
          }}
        >
          <span style={{ fontWeight: 700, color: '#3a3550' }}>{wt('targetAddress')}</span>
          {targetAddress}
        </div>

        <div style={{ fontSize: 13, color: '#5b5768', lineHeight: 1.7 }}>{wt('description')}</div>

        {/* 顺序按原型：凭证在最前。它是唯一必填项，放最后会让运营填完两栏文字才发现还要传图 */}
        <div>
          <label style={{ fontSize: 12.5, fontWeight: 700, color: '#3a3550', display: 'block', marginBottom: 6 }}>
            {wt('proofLabel')} <span style={{ color: '#e11d48' }}>*</span>
          </label>
          <ImageUploader
            value={proofKeys}
            displayUrls={padUrls(proofKeys, existing?.proofUrls)}
            onChange={setProofKeys}
            maxCount={5}
            maxSizeMB={5}
          />
          <div style={{ fontSize: 11.5, color: '#928da0', lineHeight: 1.7, marginTop: 6 }}>{wt('proofHint')}</div>
        </div>

        <div>
          <label style={{ fontSize: 12.5, fontWeight: 700, color: '#3a3550', display: 'block', marginBottom: 6 }}>
            {wt('reasonLabel')} <span style={{ color: '#e11d48' }}>*</span>
          </label>
          {/* 仍用 AutoComplete 而不是 Select：2026-09-09 产品把原因改成必填，但**维持自由输入** ——
              现场情况穷举不完，改成枚举只会逼运营选一个最接近的、反而失真。
              建议项不是白名单，后端只判非空、不校验取值，增删这里不需要改后端。 */}
          <AutoComplete
            style={{ width: '100%' }}
            placeholder={wt('reasonPlaceholder')}
            value={reason}
            onChange={setReason}
            options={ADDRESS_ABNORMAL_REASON_SUGGESTIONS.map((k) => ({ value: t(k) }))}
            filterOption={(input, option) =>
              (option?.value ?? '').toLowerCase().includes(input.toLowerCase())
            }
          >
            <Input maxLength={255} />
          </AutoComplete>
        </div>

        <div>
          <label style={{ fontSize: 12.5, fontWeight: 700, color: '#3a3550', display: 'block', marginBottom: 6 }}>
            {wt('remarkLabel')}
          </label>
          <Input.TextArea
            rows={2}
            maxLength={255}
            value={remark}
            onChange={(e) => setRemark(e.target.value)}
            placeholder={wt('remarkPlaceholder')}
          />
        </div>
      </div>
    </Modal>
  );
};

export default ReportAddressAbnormalModal;
